/**
 * 配置变更记录（Alice 页面 configAudit 的版式）：把控制面、账号服务、支付服务三处审计并成同一张表。
 *
 * 背景：本仓三个服务各有一张 append-only 的 audit_log（PRD 9 章，改动与审计在同一事务里写入）。
 *   Alice 只有一张触发器审计表，页面是「时间、操作者、类型、对象、动作、变更内容」六列加筛选；
 *   本页保持这个版式，并把原来分三个标签页的审计合并进来，来源下拉可选全部、控制面、账号、支付。
 * 设计意图：
 *   - 只看控制面时，类型、操作者、动作、对象、天数都由服务端过滤并分页（完整历史）。
 *   - 选「全部来源」或账号、支付时，每个来源取最近 200 条合并，筛选与分页在这批记录里做，页脚注明范围。
 *     账号与支付的审计接口只支持按对象精确过滤、没有时间窗口，不为这一页改它们，天数由本页按时间过滤。
 *   - Alice 的「脚本直连改动」在本仓没有对应物（没有触发器、没有直连来源），该卡换成系统与服务自动操作。
 * 关键约束：审计只读；金额、密钥之类敏感值不会出现在 detail 里（写审计的各处已约束）。
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  PRICING_PREFIXES,
  runtime2Api,
  type AuditChange,
  type AuditSource,
  type ConfigAuditFilters,
  type ConfigAuditItem,
  type ConfigAuditPage,
} from '@ap/api-client/admin-runtime2'
import { AG_runtime2 as G, errorText2 } from '@ap/i18n/admin-runtime2'
import { useI18n } from '@ap/i18n'
import { fmtDateTime } from '@ap/ui'
import { AliceBadge, OpsNote, OpsPage, Stat, Stats, TableWrap } from '../alice/kit.tsx'
import { EmptyRow, PageNav, Section, ToolInput, ToolSelect, ellipsis } from '../alice/kit-runtime2.tsx'

const PAGE = 50
const MERGE_LIMIT = 200

interface Filters {
  table: string
  actor: string
  action: string
  days: string
  entity: string
}

const EMPTY: Filters = { table: '', actor: '', action: '', days: '30', entity: '' }
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-/i

/** 合并模式下在浏览器里套用的筛选，规则与服务端 /config-audit 一致。 */
function matches(i: ConfigAuditItem, f: Filters): boolean {
  if (f.table && i.table_name !== f.table) return false
  if (f.actor && i.actor !== f.actor) return false
  if (f.action && i.action !== f.action) return false
  if (f.entity && !i.entity_key.toLowerCase().includes(f.entity.toLowerCase())) return false
  if (i.created_at && new Date(i.created_at).getTime() < Date.now() - Number(f.days) * 86_400_000) return false
  return true
}

function summarize(items: ConfigAuditItem[]) {
  return {
    pricing: items.filter((i) => PRICING_PREFIXES.has(i.table_name)).length,
    automatic: items.filter((i) => i.actor_kind === 'service' || i.actor_kind === 'system').length,
    actors: new Set(items.map((i) => i.actor)).size,
  }
}

export function Audit() {
  const { t, locale } = useI18n()
  const [source, setSource] = useState<AuditSource>('control')
  const [applied, setApplied] = useState<Filters>(EMPTY)
  const [draftEntity, setDraftEntity] = useState('')
  const [page, setPage] = useState(1)
  const [server, setServer] = useState<ConfigAuditPage | null>(null)
  const [raw, setRaw] = useState<ConfigAuditItem[] | null>(null)
  const [options, setOptions] = useState<ConfigAuditFilters | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const seq = useRef(0)
  const merged = source !== 'control'
  const fetchKey = merged ? `${source}|${applied.days}` : JSON.stringify([source, applied, page])

  useEffect(() => {
    const my = ++seq.current
    setLoading(true)
    const days = Number(applied.days)
    const task = async () => {
      if (!merged) {
        const [pageData, filters] = await Promise.all([
          runtime2Api.controlAudit({ ...applied, days, page, page_size: PAGE }),
          runtime2Api.auditFilters(90),
        ])
        return () => {
          setServer(pageData)
          setOptions(filters)
          setRaw(null)
        }
      }
      const wanted = source === 'all' ? (['control', 'identity', 'billing'] as const) : ([source] as const)
      const lists = await Promise.all(
        wanted.map(async (s) => {
          if (s === 'identity') return runtime2Api.identityAuditRows()
          if (s === 'billing') return runtime2Api.billingAuditRows()
          return (await runtime2Api.controlAudit({ days, page: 1, page_size: MERGE_LIMIT })).items
        }),
      )
      const rows = lists.flat().sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
      return () => {
        setRaw(rows)
        setServer(null)
        setOptions(null)
      }
    }
    task()
      .then((commit) => {
        if (my !== seq.current) return
        commit()
        setError(null)
      })
      .catch((e) => {
        if (my !== seq.current) return
        console.error('[audit] 加载失败', e)
        setError(e)
      })
      .finally(() => {
        if (my === seq.current) setLoading(false)
      })
    // 合并模式的筛选在浏览器里做，翻页与筛选变化不重新请求；只有来源和天数变化才重新取
  }, [fetchKey])

  const view = useMemo(() => {
    if (!merged) {
      return server && { items: server.items, total: server.total, summary: server.summary, tables: null, actors: null }
    }
    if (!raw) return null
    const hit = raw.filter((i) => matches(i, applied))
    const count = (key: (i: ConfigAuditItem) => string) => {
      const m = new Map<string, number>()
      raw.forEach((i) => m.set(key(i), (m.get(key(i)) ?? 0) + 1))
      return [...m.entries()].sort((a, b) => b[1] - a[1])
    }
    return {
      items: hit.slice((page - 1) * PAGE, page * PAGE),
      total: hit.length,
      summary: summarize(hit),
      tables: count((i) => i.table_name),
      actors: count((i) => i.actor),
    }
  }, [merged, server, raw, applied, page])

  const apply = (patch: Partial<Filters>) => {
    setApplied((f) => ({ ...f, ...patch }))
    setPage(1)
  }
  const changeSource = (next: AuditSource) => {
    setSource(next)
    setApplied((f) => ({ ...f, table: '', actor: '' }))
    setPage(1)
  }

  const typeLabel = (v: string) => (G.audit.types[v] ? t(G.audit.types[v]) : v)
  const tableOptions = options
    ? options.tables.map((o) => ({ value: o.value, label: `${typeLabel(o.value)} (${o.count})` }))
    : (view?.tables ?? []).map(([v, n]) => ({ value: v, label: `${typeLabel(v)} (${n})` }))
  const actorOptions = options
    ? options.actors.map((o) => ({ value: o.value, label: `${o.name || o.value}${o.source === 'service' || o.source === 'system' ? ` · ${t(G.audit.automaticTag)}` : ''} (${o.count})` }))
    : (view?.actors ?? []).map(([v, n]) => ({ value: v, label: `${UUID_RE.test(v) ? v.slice(0, 8) : v} (${n})` }))

  const total = view?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE))
  const dash = t(G.common.dash)

  return (
    <OpsPage eyebrow={t(G.audit.eyebrow)} title={t(G.audit.title)} desc={t(G.audit.desc)} chip={t(G.audit.chip)}>
      <Stats>
        <Stat label={t(G.audit.statTotal, { n: applied.days })} value={view ? total.toLocaleString() : dash} sub={t(G.audit.statTotalSub)} />
        <Stat label={t(G.audit.statPricing)} value={view ? view.summary.pricing.toLocaleString() : dash} sub={t(G.audit.statPricingSub)} />
        <Stat label={t(G.audit.statAutomatic)} value={view ? view.summary.automatic.toLocaleString() : dash} sub={t(G.audit.statAutomaticSub)} />
        <Stat label={t(G.audit.statActors)} value={view ? view.summary.actors.toLocaleString() : dash} sub={t(G.audit.statActorsSub)} />
      </Stats>

      <Section
        title={t(G.audit.sectionTitle)}
        copy={`${t(G.audit.sectionCopy)}${merged ? ` ${t(G.audit.mergedNote, { n: MERGE_LIMIT })}` : ''}`}
      >
        <div style={{ padding: '14px 20px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <ToolSelect
            value={source}
            onChange={(e) => changeSource(e.target.value as AuditSource)}
            options={(['control', 'identity', 'billing', 'all'] as const).map((v) => ({ value: v, label: t(G.audit.source[v]) }))}
          />
          <ToolSelect value={applied.table} onChange={(e) => apply({ table: e.target.value })} options={[{ value: '', label: t(G.audit.allTypes) }, ...tableOptions]} />
          <ToolSelect value={applied.actor} onChange={(e) => apply({ actor: e.target.value })} options={[{ value: '', label: t(G.audit.allActors) }, ...actorOptions]} />
          <ToolSelect
            value={applied.action}
            onChange={(e) => apply({ action: e.target.value })}
            options={[{ value: '', label: t(G.audit.allActions) }, ...(['UPDATE', 'INSERT', 'DELETE'] as const).map((v) => ({ value: v, label: t(G.audit.action[v]) }))]}
          />
          <ToolSelect value={applied.days} onChange={(e) => apply({ days: e.target.value })} options={Object.entries(G.audit.days).map(([value, entry]) => ({ value, label: t(entry) }))} />
          <ToolInput width={220} placeholder={t(G.audit.entityPh)} value={draftEntity} onChange={(e) => setDraftEntity(e.target.value)} onEnter={() => apply({ entity: draftEntity.trim() })} />
          <button className="btn btn-secondary btn-sm" onClick={() => apply({ entity: draftEntity.trim() })}>
            {t(G.common.query)}
          </button>
        </div>
        <TableWrap
          head={
            <>
              <th style={{ width: 110 }}>{t(G.audit.colTime)}</th>
              <th style={{ width: 170 }}>{t(G.audit.colActor)}</th>
              <th style={{ width: 120 }}>{t(G.audit.colType)}</th>
              <th style={{ width: 200 }}>{t(G.audit.colEntity)}</th>
              <th style={{ width: 70 }}>{t(G.audit.colAction)}</th>
              <th>{t(G.audit.colChanges)}</th>
            </>
          }
          pager={
            <>
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t(G.audit.totalLine, { n: total.toLocaleString() })}{merged ? ` ${t(G.audit.mergedNote, { n: MERGE_LIMIT })}` : ''}</span>
              {pages > 1 && (
                <PageNav page={page} pages={pages} label={`${page} / ${pages}`} prev={t(G.common.prev)} next={t(G.common.next)} onPage={setPage} />
              )}
            </>
          }
        >
          {error ? (
            <EmptyRow cols={6}>{t(G.common.loadFailed, { msg: errorText2(error, locale) })}</EmptyRow>
          ) : !view ? (
            <EmptyRow cols={6}>{loading ? t(G.common.loading) : t(G.audit.empty)}</EmptyRow>
          ) : view.items.length === 0 ? (
            <EmptyRow cols={6}>{t(G.audit.empty)}</EmptyRow>
          ) : (
            view.items.map((i) => (
              <tr key={i.key}>
                <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDateTime(i.created_at, locale)}</td>
                <td>
                  <ActorCell item={i} />
                </td>
                <td style={{ fontSize: 12 }}>
                  {typeLabel(i.table_name)}
                  {merged && <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{t(G.audit.source[i.source])}</div>}
                </td>
                <td className="mono" style={{ fontSize: 11, wordBreak: 'break-all' }}>
                  {i.entity_key || dash}
                </td>
                <td>
                  <ActionBadge action={i.action} />
                </td>
                <td>
                  <ChangesCell changes={i.changes} name={i.action_name} />
                </td>
              </tr>
            ))
          )}
        </TableWrap>
      </Section>
      <OpsNote>{t(G.audit.mergeNote, { n: MERGE_LIMIT })}</OpsNote>
    </OpsPage>
  )
}

function ActionBadge({ action }: { action: string }) {
  const { t } = useI18n()
  const tone = action === 'INSERT' ? 'ok' : action === 'DELETE' ? 'off' : 'info'
  return <AliceBadge tone={tone}>{t(G.audit.action[action])}</AliceBadge>
}

function ActorCell({ item }: { item: ConfigAuditItem }) {
  const { t } = useI18n()
  const automatic = item.actor_kind === 'service' || item.actor_kind === 'system'
  const label = item.actor_name || (UUID_RE.test(item.actor) ? item.actor.slice(0, 8) : item.actor)
  return (
    <>
      <div style={{ fontWeight: 500, fontSize: 12, ...ellipsis }} title={item.actor}>
        {label}
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{automatic ? t(G.audit.automaticTag) : item.client_addr}</div>
    </>
  )
}

/** 变更内容：最多列 4 个字段，其余折叠成一行提示；没有字段时显示动作名。 */
function ChangesCell({ changes, name }: { changes: AuditChange[]; name: string }) {
  const { t } = useI18n()
  if (changes.length === 0) {
    return (
      <span className="mono" style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
        {name}
      </span>
    )
  }
  return (
    <div style={{ fontSize: 12, lineHeight: 1.8 }}>
      {changes.slice(0, 4).map((c, idx) => (
        <div key={`${c.field}-${idx}`}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
            {c.field}
          </span>{' '}
          {c.before == null ? (
            <span style={{ color: 'var(--success-text)' }}>{c.after}</span>
          ) : c.after == null ? (
            <span style={{ color: 'var(--text-tertiary)', textDecoration: 'line-through' }}>{c.before}</span>
          ) : (
            <>
              <span style={{ color: 'var(--text-tertiary)' }}>{c.before}</span> <span style={{ color: 'var(--text-tertiary)' }}>→</span>{' '}
              <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{c.after}</span>
            </>
          )}
        </div>
      ))}
      {changes.length > 4 && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{t(G.audit.moreFields, { n: changes.length - 4 })}</div>}
    </div>
  )
}
